import { useContext, useEffect, useRef, useState } from 'react';
import { apiClient, AuthContext } from '../App';

function InvitationPages({ gameId, refreshKey, renderPlayer, testId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const busy = useRef(false);

  const load = async (after = null, reset = false) => {
    if (busy.current && !reset) return;
    const ticket = ++generation.current;
    busy.current = true;
    setLoading(true); setError('');
    if (reset) { setRows([]); setTotal(null); setCursor(null); }
    try {
      const { data } = await apiClient.get(`/games/${gameId}/pending-invitations`, {
        params: after ? { after } : {},
      });
      if (ticket !== generation.current) return;
      if (!Array.isArray(data.players) || !Number.isInteger(data.total_count)) {
        throw new Error('Invalid invitation page');
      }
      setRows(previous => {
        const byId = new Map((reset ? [] : previous).map(row => [row.user_id, row]));
        data.players.forEach(row => byId.set(row.user_id, row));
        return [...byId.values()];
      });
      setTotal(data.total_count);
      setCursor(data.next_cursor || null);
    } catch (failure) {
      if (ticket !== generation.current) return;
      if ([403, 404].includes(failure.response?.status)) {
        setRows([]); setTotal(null); setCursor(null);
      }
      setError('Could not load pending invitations. Try again.');
    } finally {
      if (ticket === generation.current) {
        busy.current = false; setLoading(false);
      }
    }
  };
  useEffect(() => {
    load(null, true);
    return () => { generation.current += 1; busy.current = false; };
    // The keyed parent resets the entire list when session/account changes.
  }, [gameId, refreshKey]);

  return <div aria-busy={loading} data-testid="pending-invitation-pages">
    {total !== null && <p className="mt-2 text-xs text-zinc-600" role="status">
      {rows.length <= total ? `Showing ${rows.length} of ${total} pending invitees.`
        : `List changed: ${rows.length} loaded, ${total} currently pending. Refresh invitations.`}
    </p>}
    <p className="mt-1 text-xs text-zinc-500">Invitations can change while you browse. Refresh for the latest list.</p>
    <ul className="mt-2 space-y-2" data-testid={testId}>{rows.map(renderPlayer)}</ul>
    {total === 0 && <p className="text-sm text-zinc-500">No pending invitations.</p>}
    {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
    <div className="mt-2 flex flex-wrap gap-2">
      {(cursor || error) && <button type="button" disabled={loading}
        className="min-h-11 rounded-lg border px-3 text-sm" onClick={() => load(cursor)}>
        {error ? 'Try again' : 'Load more'}
      </button>}
      <button type="button" disabled={loading} className="min-h-11 rounded-lg border px-3 text-sm"
        onClick={() => load(null, true)}>Refresh invitations</button>
      {loading && <span role="status" className="self-center text-sm text-zinc-500">Loading invitations...</span>}
    </div>
  </div>;
}

export default function PendingInvitationList(props) {
  const { user } = useContext(AuthContext);
  return <InvitationPages key={JSON.stringify([props.gameId, user?.id || null])} {...props} />;
}
